import { Badge, Button, Flex, Table, Text } from "@radix-ui/themes";
import { useState } from "react";
import { Search } from "@/components/shared/inputs/search";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import { useParams } from "react-router-dom";
import { ExpandableText } from "@/v2/components/expandableText/ExpandableText";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useRegressionApi } from "@/v2/entities/regression/Regression.api";
import { useQuery } from "react-query";

export function RegressionTestDetail() {
  const { id } = useParams();

  const { getRegressionTestResults } = useRegressionApi();

  const { data: results, isLoading: resultsLoading } = useQuery({
    queryKey: ["regression-test-results", id],
    queryFn: () => getRegressionTestResults(id || ""),
    enabled: !!id,
  });
  const [searchPhrase, setSearchPhrase] = useState("");
  const navigate = useNavigate();

  const isLoading = resultsLoading;

  const exportToExcel = () => {
    if (!results || results.length === 0) return;

    const filteredResults = results.filter(
      (result: any) =>
        result.userComment?.comment
          ?.toLowerCase()
          .includes(searchPhrase.toLowerCase()) ||
        result.agentComment?.comment
          ?.toLowerCase()
          .includes(searchPhrase.toLowerCase()) ||
        result.aiResponse?.toLowerCase().includes(searchPhrase.toLowerCase())
    );

    const excelData = filteredResults.map((result: any) => ({
      "Test ID": result.regressionTest?.id?.slice(0, 8) + "...",
      Question:
        result.userComment?.comment ?? result.regressionSetItem?.question,
      "Expected Answer":
        result.agentComment?.comment ||
        result.regressionSetItem?.expectedAnswer,
      "AI Response": result.aiResponse,
      "Similarity (%)": (result.similarity * 100).toFixed(1),
      Created: new Date(result.createdAt).toLocaleDateString(),
      Status:
        result.similarity > 0.8
          ? "Pass"
          : result.similarity > 0.6
          ? "Warning"
          : "Fail",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Regression Test Results"
    );

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });

    const filename = `regression-test-${id?.slice(-8)}-results-${
      new Date().toISOString().split("T")[0]
    }.xlsx`;
    saveAs(data, filename);
  };

  return (
    <Flex direction={"column"} gap={"8"} px={"8"} py={"8"}>
      <Flex direction={"row"} gap={"2"} justify={"between"}>
        <Flex direction={"column"} gap={"2"}>
          <Flex align="center" gap="2">
            <Button
              variant="ghost"
              size="1"
              onClick={() => navigate("/automation-center/regression-sets")}
            >
              <ArrowLeftIcon />
            </Button>
            <h3 style={{ fontWeight: "500" }}>
              Regression Test "{id?.slice(-8)}" Results
            </h3>
          </Flex>
          <Text size="2" color="gray">
            View all regression test results for test
          </Text>
        </Flex>
      </Flex>

      <Flex direction={"column"} gap={"2"}>
        <Flex direction={"row"} gap={"2"} justify={"between"}>
          <Flex gap={"2"} align={"center"}>
            <Text size={"3"} weight={"medium"}>
              Test Results
            </Text>
            <Badge color="gray">{results?.length || 0}</Badge>
          </Flex>
          <Flex gap={"2"} align={"center"}>
            <Search
              initialValue={searchPhrase}
              placeholder="Search test results"
              setSearchPhrase={setSearchPhrase}
              debounce={50}
            />
            <Button
              variant="outline"
              onClick={exportToExcel}
              disabled={!results || results.length === 0}
            >
              Export to Excel
            </Button>
          </Flex>
        </Flex>

        <Table.Root>
          <Table.Header>
            <Table.Row className=" text-gray-500 font-normal">
              <Table.ColumnHeaderCell className=" font-normal w-1/6">
                Test ID
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className=" font-normal w-1/4">
                Question
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className=" font-normal w-1/4">
                Expected Answer
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className=" font-normal w-1/4">
                AI Response
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className=" font-normal w-1/6">
                Similarity
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className=" font-normal w-1/6">
                Created
              </Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {isLoading && (
              <Table.Row>
                <Table.Cell colSpan={5}>
                  <Flex align="center" justify="center" py="6">
                    <Text size="2" color="gray">
                      Loading...
                    </Text>
                  </Flex>
                </Table.Cell>
              </Table.Row>
            )}
            {!isLoading && (!results || results.length === 0) && (
              <Table.Row>
                <Table.Cell colSpan={5}>
                  <Flex
                    align="center"
                    justify="center"
                    py="6"
                    direction="column"
                    gap="2"
                  >
                    <Text size="2" color="gray">
                      No test results found
                    </Text>
                    <Text size="2" color="gray">
                      Run some regression tests to see results here
                    </Text>
                    <Button
                      color="gray"
                      highContrast
                      onClick={() =>
                        navigate("/automation-center/regression-sets")
                      }
                      className="mt-2"
                    >
                      Go to Regression Sets
                    </Button>
                  </Flex>
                </Table.Cell>
              </Table.Row>
            )}
            {!isLoading &&
              results
                ?.filter(
                  (result: any) =>
                    result.userComment?.comment
                      ?.toLowerCase()
                      .includes(searchPhrase.toLowerCase()) ||
                    result.agentComment?.comment
                      ?.toLowerCase()
                      .includes(searchPhrase.toLowerCase()) ||
                    result.aiResponse
                      ?.toLowerCase()
                      .includes(searchPhrase.toLowerCase())
                )
                .map((result: any) => (
                  <Table.Row key={result.id}>
                    <Table.Cell>
                      <Text size="1" className="font-mono">
                        {result.regressionTest?.id?.slice(0, 8)}...
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <ExpandableText
                        text={
                          result.userComment?.comment ??
                          result.regressionSetItem.question
                        }
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <ExpandableText
                        text={
                          result.agentComment?.comment ||
                          result.regressionSetItem.expectedAnswer
                        }
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <ExpandableText text={result.aiResponse} />
                    </Table.Cell>
                    <Table.Cell>
                      <Badge
                        color={
                          result.similarity > 0.8
                            ? "green"
                            : result.similarity > 0.6
                            ? "yellow"
                            : "red"
                        }
                        variant="soft"
                      >
                        {(result.similarity * 100).toFixed(1)}%
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      {new Date(result.createdAt).toLocaleDateString()}
                    </Table.Cell>
                  </Table.Row>
                ))}
          </Table.Body>
        </Table.Root>
      </Flex>
    </Flex>
  );
}
